import type http from 'http'

import { baseGlobals } from '@root/typescript/baseGlobals'

import { Object } from '../Object/Object'
import { is } from '../is/is'

import type {
  TRoutes,
  TRouteParams,
  IClassRouter,
  TRouteCallback,
  TServerCallback,
  TClientCallback,
  TNotFoundCallback
} from './definitions'

import getParamsByUrlAndRoute from './utils/getParamsByUrlAndRoute'
import getRoutesByPath        from './utils/getRoutesByPath'
import callCallback from './utils/callCallback'

const NOT_FOUND_ROUTES = 0

export default class Router implements IClassRouter {
  protected afterAll?: TRouteCallback
  protected beforeAll?: TRouteCallback
  protected notFoundCallback?: TNotFoundCallback | undefined
  protected headRequestCallback?: TRouteCallback | undefined

  protected routes: TRoutes = {}

  protected readonly namespace: string

  public constructor (namespace = '') {
    this.namespace = namespace
  }

  /**
   * Egyetlen route és hozzátartozó callback hozzáadása.
   * @param route    - Az adott route.
   * @param callback - Mi a callbackje.
   */
  public addRoute <T extends string = string> (route: T, callback: TRouteCallback): void {
    if (!baseGlobals.isTest) {
      const ns = this.namespace ? `[${ this.namespace }]` : ''
      // eslint-disable-next-line no-console -- Mehet ki console-ra, hogy mit adtunk hozzá.
      console.log(`[ROUTER]${ ns }[addRoute]`, route)
    }

    this.routes[route] = callback
  }

  /**
   * Egy szettnyi route hozzáadása.
   * A meglévő route-okat módosítja.
   * @param routes - Egy szettnyi route.
   */
  public addRoutes (routes: TRoutes): void {
    Object.getEntries(routes).forEach(([ route, callback ]) => {
      this.addRoute(route, callback)
    })
  }

  /**
   * Egyetlen url feldolgozása, majd az eredménynek megfelelő callback(ek) meghívása.
   * @param path - Bármilyen valid URL path.
   * @param req  - A request.
   * @param res  - A response.
   * @throws Ha nincs path, vagy route, akkor hibát dobunk.
   */
  public run (path?: string, req?: http.IncomingMessage, res?: http.ServerResponse): Promise<void> {
    if (!is.string(path)) {
      throw new TypeError('[ROUTER][run] a path nincs megadva.')
    }

    if (Object.getKeys(this.routes).length <= NOT_FOUND_ROUTES) {
      throw new TypeError('[ROUTER][run] nincsenek route-ok megadva.')
    }

    const [ querylessPath ] = path.split('?')

    return this.runRoutes(querylessPath, req, res)
  }

  /**
   * Bármilyen route UTÁN ez fog megfutni.
   * @param cb - A callback, amit megfuttatunk.
   */
  public setAfterAll (cb: TRouteCallback): void {
    this.afterAll = cb
  }

  /**
   * Bármilyen route ELŐTT ez fog megfutni.
   * @param cb - A callback, amit megfuttatunk.
   */
  public setBeforeAll (cb: TRouteCallback): void {
    this.beforeAll = cb
  }

  /**
   * Egy notFoundCallback beállítása.
   * Ez hívódik meg, ha nem talál a Router a megadott path-hez passzoló route-ot.
   * @param callback - A callback, amit meg fog hívni a Router.
   */
  public setNotFound (callback: TNotFoundCallback): void {
    this.notFoundCallback = callback
  }

  /**
   * Az összes route megfuttatása, amire passzol a path.
   * @param path - A path, amit vizsgálunk.
   * @param req  - ServerRouter esetén a request.
   * @param res  - ServerRouter esetén a response.
   */
  protected async runRoutes (path: string, req?: http.IncomingMessage, res?: http.ServerResponse): Promise<void> {
    // Ha van beforeAll callback, akkor hívjuk meg.
    if (this.beforeAll) {
      await callCallback({
        callback: this.beforeAll,
        path,
        params: {},
        req,
        res
      })
    }

    // A path alapján az illeszkedő route-ok.
    const routes = getRoutesByPath(path, this.routes)

    // A route-ok path-jei.
    const routeKeys = Object.getKeys(routes)

    // Ha nincs egy talált route sem, akkor fixen 404.
    if (is.empty(routeKeys)) {
      this.runNotFoundCallback(path, {}, req, res)
    }
    else {
      if (req?.method === 'HEAD') {
        if (this.headRequestCallback) {
          // @ts-expect-error Nem érek most rá.
          this.headRequestCallback({}, req, res)
        }
        else {
          // Ha HEAD, akkor ne hívjuk meg a callbackeket, csak szóljunk, hogy oké, van ilyen route.
          res?.end()
        }

        return
      }

      // Végig az összes megtalált route-on.
      await Promise.all(routeKeys.map((route) => {
        // GET paraméterek kiszedése a route-ból.
        const params = getParamsByUrlAndRoute(path, route)
        // A path-hez megadott callback.
        const callback = routes[route]

        // A callback meghívása az összes paraméterrel.
        return callCallback({
          callback,
          path,
          params,
          req,
          res
        })
      }))
    }

    // Ha van afterAll callback, hívjuk meg.
    if (this.afterAll) {
      await callCallback({
        callback: this.afterAll,
        path,
        params: {},
        req,
        res
      })
    }
  }

  /**
   * Ha a path-re nem illett egyik route sem, akkor ez hívódik meg.
   * @param path   - A path, amihez nem találtunk route-ot.
   * @param params - A paraméterek.
   * @param req    - ServerRouter esetén a request.
   * @param res    - ServerRouter esetén a response.
   */
  protected runNotFoundCallback ( // eslint-disable-line @typescript-eslint/max-params -- Legacy.
    path: string,
    params: TRouteParams,
    req?: http.IncomingMessage,
    res?: http.ServerResponse
  ): void {
    if (this.notFoundCallback) {
      if (req && res) {
        (this.notFoundCallback as unknown as TServerCallback)(params, req, res)
      }
      else {
        (this.notFoundCallback as unknown as TClientCallback)(params, '', path)
      }
    }
  }
}
